import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import http from '@/services/http';
import { queryKeys } from '@/queries/_shared/queryKeys';

const VIDEO_COURSE_ENDPOINTS = {
  courses: '/edu-course',
  subjects: '/edu-subject',
  videos: '/edu-video'
};

function getResponseList(response) {
  const body = response?.data?.body ?? response?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body?.data)) return body.data;
  return [];
}

function getOptionText(item, index, fallback) {
  return item?.name || item?.title || item?.label || item?.course || item?.subject || `${fallback} ${index + 1}`;
}

function getOptionValue(item, index) {
  return item?.id || item?.course_id || item?.subject_id || item?.value || index;
}

function getVideoUrl(item) {
  return item?.url || item?.video_url || item?.link || item?.href || item?.src || '';
}

function getVideoTitle(item, index, fallback) {
  return (
    item?.title ||
    item?.name ||
    item?.video_name ||
    item?.lesson_name ||
    item?.subject_name ||
    `${fallback} ${index + 1}`
  );
}

function getVideoMeta(item) {
  const meta = [];

  if (item?.course_name || item?.course) {
    meta.push(item.course_name || item.course);
  }

  if (item?.subject_name || item?.subject) {
    meta.push(item.subject_name || item.subject);
  }

  return meta.join(' • ');
}

function getYoutubeOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return 'https://web.edumark.kz';
}

function getYoutubeEmbedUrl(url) {
  const shortMatch = url.match(/youtu\.be\/([^?&/]+)/);
  const watchMatch = url.match(/[?&]v=([^?&/]+)/);
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&/]+)/);
  const videoId =
    (shortMatch && shortMatch[1]) ||
    (watchMatch && watchMatch[1]) ||
    (embedMatch && embedMatch[1]) ||
    '';

  if (!videoId) return url;

  return `https://www.youtube.com/embed/${videoId}?origin=${encodeURIComponent(getYoutubeOrigin())}`;
}

function getVimeoEmbedUrl(url) {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (match && match[1]) {
    return `https://player.vimeo.com/video/${match[1]}`;
  }

  return url;
}

function getEmbedUrl(url) {
  if (!url) return '';
  if (url.includes('youtu')) return getYoutubeEmbedUrl(url);
  if (url.includes('vimeo')) return getVimeoEmbedUrl(url);
  return url;
}

export async function fetchVideoCourseCourses(fallback = 'Класс') {
  const response = await http.get(VIDEO_COURSE_ENDPOINTS.courses);
  const list = getResponseList(response);

  return list.map((item, index) => ({
    text: getOptionText(item, index, fallback),
    value: getOptionValue(item, index),
    raw: item
  }));
}

export async function fetchVideoCourseSubjects(courseId, fallback = 'Предмет') {
  if (!courseId) return [];

  const response = await http.get(VIDEO_COURSE_ENDPOINTS.subjects, {
    params: { course_id: courseId }
  });
  const list = getResponseList(response);

  return list.map((item, index) => ({
    text: getOptionText(item, index, fallback),
    value: getOptionValue(item, index),
    raw: item
  }));
}

export async function fetchVideoCourseVideos(params = {}, lessonFallback = 'Видео урок') {
  const response = await http.get(VIDEO_COURSE_ENDPOINTS.videos, {
    params
  });
  const list = getResponseList(response);

  return list
    .map((item, index) => {
      const url = getVideoUrl(item);
      return {
        id: item?.id || item?.video_id || `${params.course_id || 'all'}-${params.subject_id || 'all'}-${index}`,
        title: getVideoTitle(item, index, lessonFallback),
        meta: getVideoMeta(item),
        embedUrl: getEmbedUrl(url),
        raw: item
      };
    })
    .filter((item) => item.embedUrl);
}

export function useVideoCourseCoursesQuery(fallbackLabel, options = {}) {
  const fallback = computed(() => fallbackLabel?.value || fallbackLabel || 'Класс');

  return useQuery({
    queryKey: computed(() => queryKeys.videoCourse.courses(fallback.value)),
    queryFn: () => fetchVideoCourseCourses(fallback.value),
    staleTime: 5 * 60 * 1000,
    ...options
  });
}

export function useVideoCourseSubjectsQuery(courseId, fallbackLabel, options = {}) {
  const resolvedCourseId = computed(() => Number(courseId?.value ?? courseId ?? 0) || 0);
  const fallback = computed(() => fallbackLabel?.value || fallbackLabel || 'Предмет');

  return useQuery({
    queryKey: computed(() => queryKeys.videoCourse.subjects(resolvedCourseId.value, fallback.value)),
    queryFn: () => fetchVideoCourseSubjects(resolvedCourseId.value, fallback.value),
    enabled: computed(() => resolvedCourseId.value > 0),
    staleTime: 5 * 60 * 1000,
    ...options
  });
}

export function useVideoCourseVideosQuery(filters, lessonFallback, options = {}) {
  const resolvedFilters = computed(() => {
    const value = filters?.value ?? filters ?? {};
    const next = {};

    if (value?.course_id) next.course_id = value.course_id;
    if (value?.subject_id) next.subject_id = value.subject_id;

    return next;
  });

  const fallback = computed(() => lessonFallback?.value || lessonFallback || 'Видео урок');

  return useQuery({
    queryKey: computed(() => queryKeys.videoCourse.videos(resolvedFilters.value, fallback.value)),
    queryFn: () => fetchVideoCourseVideos(resolvedFilters.value, fallback.value),
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
    ...options
  });
}
